import React, { useState } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { updateDoc, doc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { Switch, TextField, Select, MenuItem, Button, Alert } from '../../ui';
import { useToast } from '../../../hooks/useToast';
import { useParams } from 'react-router-dom';
import { useSalon } from '../../../contexts/SalonContext';
import CustomNotice from '../../booking/shared/CustomNotice';

// Componente para exibir a pré-visualização do aviso exatamente como aparecerá no sistema
const CustomNoticePreview = ({ message, variant }) => {
  if (!message) return null;
  
  return (
    <div className="border border-gray-200 p-2 rounded-lg">
      <CustomNotice message={message} variant={variant} />
    </div>
  );
};

const NoticesSettings = () => {
  const { salonId } = useParams();
  const { salonConfig, loading: salonLoading } = useSalon();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  
  const customNotices = salonConfig?.customNotices || {
    pricingNotice: { enabled: true, message: "Os preços podem variar de acordo com o profissional selecionado", variant: "gray" },
    generalNotice: { enabled: false, message: "", variant: "gray", displayLocation: "all" }
  };
  
  const { control, handleSubmit, formState: { isDirty, isSubmitting }, watch } = useForm({
    defaultValues: customNotices
  });

  // Observar as mudanças nos campos em tempo real
  const pricingNoticeEnabled = watch('pricingNotice.enabled');
  const pricingNoticeMessage = watch('pricingNotice.message');
  const pricingNoticeVariant = watch('pricingNotice.variant');
  
  const generalNoticeEnabled = watch('generalNotice.enabled');
  const generalNoticeMessage = watch('generalNotice.message');
  const generalNoticeVariant = watch('generalNotice.variant');

  // Mapeamento de cores do Tailwind para a pré-visualização do alerta
  const colorToVariantMap = {
    'blue': 'info',
    'green': 'success',
    'yellow': 'warning',
    'red': 'error',
    'purple': 'info',
    'pink': 'info',
    'indigo': 'info',
    'teal': 'info',
    'gray': 'info'
  };

  // Função para obter o estilo de cor para pré-visualização
  const getVariantForPreview = (color) => {
    return colorToVariantMap[color] || 'info';
  };

  const onSubmit = async (data) => {
    if (!salonId) {
      showToast('ID do salão não encontrado', 'error');
      return;
    }
    
    setLoading(true);
    
    try {
      await updateDoc(doc(db, `salons/${salonId}`), {
        customNotices: data
      });
      showToast('Configurações de avisos atualizadas com sucesso', 'success');
    } catch (error) {
      console.error('Erro ao atualizar avisos:', error);
      showToast('Erro ao atualizar configurações. Tente novamente.', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (salonLoading) {
    return (
      <div className="p-4 flex justify-center items-center">
        <div className="animate-pulse text-center">
          <p className="text-gray-500">Carregando configurações...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <h2 className="text-xl font-semibold mb-4">Configuração de Avisos</h2>
      
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <h3 className="text-lg font-medium mb-3">Aviso de Preços Customizados</h3>
          
          <div className="flex items-center mb-4">
            <Controller
              name="pricingNotice.enabled"
              control={control}
              render={({ field }) => (
                <Switch 
                  checked={field.value} 
                  onChange={field.onChange}
                />
              )}
            />
            <span className="ml-2">Ativar aviso de preços customizados</span>
          </div>
          
          <Controller
            name="pricingNotice.message"
            control={control}
            render={({ field }) => (
              <TextField
                label="Mensagem personalizada"
                fullWidth
                multiline
                rows={2}
                disabled={!pricingNoticeEnabled}
                {...field}
              />
            )}
          />
          
          {/* Seletor de cor para aviso de preços customizados */}
          <div className="mt-4">
            <Controller
              name="pricingNotice.variant"
              control={control}
              render={({ field }) => (
                <Select
                  label="Cor do aviso"
                  disabled={!pricingNoticeEnabled}
                  {...field}
                >
                  <MenuItem value="blue">Azul</MenuItem>
                  <MenuItem value="green">Verde</MenuItem>
                  <MenuItem value="red">Vermelho</MenuItem>
                  <MenuItem value="yellow">Amarelo</MenuItem>
                  <MenuItem value="purple">Roxo</MenuItem>
                  <MenuItem value="pink">Rosa</MenuItem>
                  <MenuItem value="indigo">Índigo</MenuItem>
                  <MenuItem value="teal">Verde-azulado</MenuItem>
                  <MenuItem value="gray">Cinza</MenuItem>
                </Select>
              )}
            />
          </div>
          
          {/* Pré-visualização para aviso de preços customizados */}
          {pricingNoticeEnabled && pricingNoticeMessage && (
            <div className="mt-4">
              <p className="text-sm font-medium mb-2">Pré-visualização:</p>
              <CustomNoticePreview 
                message={pricingNoticeMessage}
                variant={pricingNoticeVariant || 'gray'}
              />
            </div>
          )}
          
          <div className="mt-4">
            <Alert severity="info" className="text-sm">
              Este aviso só será exibido se houver profissionais com preços customizados no sistema.
            </Alert>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <h3 className="text-lg font-medium mb-3">Aviso Personalizado</h3>
          
          <div className="flex items-center mb-4">
            <Controller
              name="generalNotice.enabled"
              control={control}
              render={({ field }) => (
                <Switch 
                  checked={field.value} 
                  onChange={field.onChange}
                />
              )}
            />
            <span className="ml-2">Ativar aviso personalizado</span>
          </div>
          
          <Controller
            name="generalNotice.message"
            control={control}
            render={({ field }) => (
              <TextField
                label="Mensagem personalizada"
                fullWidth
                multiline
                rows={3}
                disabled={!generalNoticeEnabled}
                {...field}
              />
            )}
          />
          
          <div className="grid grid-cols-2 gap-4 mt-4">
            <Controller
              name="generalNotice.variant"
              control={control}
              render={({ field }) => (
                <Select
                  label="Cor do aviso"
                  disabled={!generalNoticeEnabled}
                  {...field}
                >
                  <MenuItem value="blue">Azul</MenuItem>
                  <MenuItem value="green">Verde</MenuItem>
                  <MenuItem value="red">Vermelho</MenuItem>
                  <MenuItem value="yellow">Amarelo</MenuItem>
                  <MenuItem value="purple">Roxo</MenuItem>
                  <MenuItem value="pink">Rosa</MenuItem>
                  <MenuItem value="indigo">Índigo</MenuItem>
                  <MenuItem value="teal">Verde-azulado</MenuItem>
                  <MenuItem value="gray">Cinza</MenuItem>
                </Select>
              )}
            />
            
            <Controller
              name="generalNotice.displayLocation"
              control={control}
              render={({ field }) => (
                <Select
                  label="Exibir em"
                  disabled={!generalNoticeEnabled}
                  {...field}
                >
                  <MenuItem value="all">Todas as etapas</MenuItem>
                  <MenuItem value="serviceSelection">Seleção de serviços</MenuItem>
                  <MenuItem value="dateTimeSelection">Seleção de data/hora</MenuItem>
                  <MenuItem value="clientForm">Formulário do cliente</MenuItem>
                </Select>
              )}
            />
          </div>
          
          {/* Pré-visualização */}
          {generalNoticeEnabled && generalNoticeMessage && (
            <div className="mt-4">
              <p className="text-sm font-medium mb-2">Pré-visualização:</p>
              <CustomNoticePreview 
                message={generalNoticeMessage}
                variant={generalNoticeVariant || 'gray'}
              />
            </div>
          )}
        </div>
        
        <Button 
          type="submit"
          variant="contained"
          disabled={!isDirty || isSubmitting}
          loading={loading}
        >
          Salvar configurações
        </Button>
      </form>
    </div>
  );
};

export default NoticesSettings; 